import React from "react";
import { TrendingUp, Users, Database, Zap, ArrowRight, Play, FolderPlus, BarChart3, Clock, CheckCircle, List, BarChart2, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getProjects, getDataSets } from "common-lib";
import { getPagedInspModelList } from "../../services/modelService";
import { useTheme } from "../../theme/useTheme";
import { Button, Box, Typography, Grid } from "@mui/material";
import { SxProps } from "@mui/system";
import ServerTable from "../../components/server/ServerTable";
import ServerStatusWidget from "../../components/server/ServerStatusWidget";
import PageShell from "../../components/new/PageShell";

export default function DashboardPage() {
  const navigate = useNavigate();

  // 카드 숫자는 각 목록 API가 같이 주는 total만 쓴다. 개수 전용 API는 없음.
  // ponytail: GET /dataset 은 데이터셋 1건마다 gRPC를 타므로, 데이터셋이 수백 개가 되면 /dataset/count 를 따로 파야 한다.
  const countOptions = { staleTime: 60_000, retry: false } as const;
  const { data: projectCount } = useQuery(["dashboard", "projects"], () => getProjects({ page: 1, size: 1 }).then((r) => r.projects.total as number), countOptions);
  const { data: datasetCount } = useQuery(["dashboard", "datasets"], () => getDataSets({ page: 1, size: 1 }).then((r) => r.data.total_count as number), countOptions);
  const { data: inspModelCount } = useQuery(["dashboard", "insp-models"], () => getPagedInspModelList({ page: 1, size: 1 }).then((r) => r.total_count), countOptions);

  // 로딩/실패 구분 없이 숫자가 없으면 "–". 대시보드 카드에 에러 문구를 띄울 만한 값이 아니다.
  const fmtCount = (n?: number) => (typeof n === "number" ? n.toLocaleString() : "–");

  const SF = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif`;

  // 공통 토큰
  const TOKEN = {
    card: "var(--dk-surface, white)",
    border: "var(--dk-border, rgba(0,0,0,0.09))",
    shadow: "0 1px 3px rgba(0,0,0,0.06)",
    text: "var(--dk-text, #1d1d1f)",
    sub: "var(--dk-text-2, #6e6e73)",
    hint: "var(--dk-text-3, #aeaeb2)",
    blue: "#2563eb",
    purple: "#7c3aed",
    green: "#16a34a",
    orange: "#ea580c",
  };

  const MacCard = ({ children, sx = {}, onClick }: { children: React.ReactNode; sx?: SxProps; onClick?: () => void }) => (
    <Box
      onClick={onClick}
      sx={{
        background: TOKEN.card,
        borderRadius: "14px",
        border: `0.5px solid ${TOKEN.border}`,
        boxShadow: TOKEN.shadow,
        ...sx,
      }}
    >
      {children}
    </Box>
  );

  const MacButton = ({ id, children, primary, onClick }: { id: string; children: React.ReactNode; primary?: boolean; onClick?: () => void }) => (
    <Button
      id={id}
      fullWidth
      onClick={onClick}
      sx={{
        height: 38,
        borderRadius: "10px",
        border: `0.5px solid ${primary ? TOKEN.blue : "var(--dk-border, rgba(0,0,0,0.14))"}`,
        background: primary ? TOKEN.blue : "var(--dk-surface-2, #f5f5f7)",
        color: primary ? "white" : TOKEN.text,
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: SF,
        textTransform: "none",
        letterSpacing: "-0.01em",
        boxShadow: primary ? "0 1px 4px rgba(37,99,235,0.28)" : "none",
        "&:hover": { background: primary ? "#1d4ed8" : "var(--dk-surface-3, #ebebed)" },
        mb: "8px",
      }}
    >
      {children}
    </Button>
  );

  return (
    <PageShell
      title="대시보드"
      subtitle="데이터 분류분석 솔루션"
      actions={
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{
              fontSize: "11px",
              color: TOKEN.hint,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              fontFamily: SF,
            }}
          >
            오늘
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: TOKEN.text, mt: "2px", fontFamily: SF }}>{new Date().toLocaleDateString("ko-KR")}</Typography>
        </Box>
      }
    >
      <Box sx={{ fontFamily: SF }}>
        {/* Stat Cards */}
        <Grid container spacing={1.5} sx={{ mb: "20px" }}>
          {[
            {
              label: "활성 프로젝트",
              value: fmtCount(projectCount),
              icon: <FolderPlus size={18} />,
              iconBg: "#e8f0fe",
              iconColor: TOKEN.blue,
              trend: "등록됨",
              trendSub: "전체 프로젝트",
              trendColor: TOKEN.green,
              TrendIcon: CheckCircle,
            },
            // {
            //   label: "학습 중인 모델",
            //   value: "8",
            //   icon: <Zap size={18} />,
            //   iconBg: "#f0eeff",
            //   iconColor: TOKEN.purple,
            //   trend: "진행 중",
            //   trendSub: "평균 2시간 남음",
            //   trendColor: TOKEN.orange,
            //   TrendIcon: Clock,
            // },
            {
              label: "데이터셋",
              value: fmtCount(datasetCount),
              icon: <Database size={18} />,
              iconBg: "#e8f5ea",
              iconColor: TOKEN.green,
              trend: "준비됨",
              trendSub: "전체 데이터셋",
              trendColor: TOKEN.green,
              TrendIcon: CheckCircle,
            },
            {
              label: "검사모델",
              value: fmtCount(inspModelCount),
              icon: <BarChart2 size={18} />,
              iconBg: "#fff3e8",
              iconColor: TOKEN.orange,
              trend: "사용 가능",
              trendSub: "전체 검사모델",
              trendColor: TOKEN.green,
              TrendIcon: CheckCircle,
            },
          ].map((s, i) => (
            <Grid item xs={4} key={i}>
              <MacCard sx={{ p: "16px" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "14px" }}>
                  <Box>
                    <Typography sx={{ fontSize: "12px", color: TOKEN.sub, letterSpacing: "-0.01em", fontFamily: SF, mb: "4px" }}>{s.label}</Typography>
                    <Typography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 700,
                        color: TOKEN.text,
                        letterSpacing: "-0.02em",
                        fontFamily: SF,
                      }}
                    >
                      {s.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: s.iconBg,
                      color: s.iconColor,
                      ".dark &": { background: `color-mix(in srgb, ${s.iconColor} 20%, transparent)` },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {s.icon}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px" }}>
                  <s.TrendIcon size={13} color={s.trendColor} />
                  <Typography sx={{ fontSize: "12px", color: s.trendColor, fontFamily: SF }}>{s.trend}</Typography>
                  <Typography sx={{ fontSize: "12px", color: TOKEN.hint, fontFamily: SF }}>{s.trendSub}</Typography>
                </Box>
              </MacCard>
            </Grid>
          ))}
        </Grid>

        {/* Action Cards */}
        <Grid container spacing={1.75} sx={{ mb: "20px" }}>
          {/* 프로젝트 */}
          <Grid item xs={6}>
            <MacCard sx={{ p: "24px" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  background: TOKEN.blue,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "18px",
                }}
              >
                <FolderPlus size={22} />
              </Box>
              <Typography
                sx={{
                  fontSize: "17px",
                  fontWeight: 700,
                  color: TOKEN.text,
                  letterSpacing: "-0.02em",
                  mb: "6px",
                  fontFamily: SF,
                }}
              >
                프로젝트 관리
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: TOKEN.sub,
                  lineHeight: 1.55,
                  mb: "18px",
                  letterSpacing: "-0.01em",
                  fontFamily: SF,
                }}
              >
                AI 모델 개발 프로젝트를 생성하고 팀과 협업하세요. 버전 관리를 통해 효율적인 개발이 가능합니다.
              </Typography>
              <MacButton id="navigate-create-project" primary onClick={() => navigate("/main/projects/create")}>
                <Play size={14} style={{ marginRight: 6 }} /> 새 프로젝트 시작
              </MacButton>
              <MacButton id="navigate-project-list" onClick={() => navigate("/main/projects")}>
                <List size={14} style={{ marginRight: 6 }} /> 프로젝트 메뉴로 이동
              </MacButton>
            </MacCard>
          </Grid>

          {/* 모델 */}
          <Grid item xs={6}>
            <MacCard sx={{ p: "24px" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  background: TOKEN.purple,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "18px",
                }}
              >
                <Zap size={22} />
              </Box>
              <Typography
                sx={{
                  fontSize: "17px",
                  fontWeight: 700,
                  color: TOKEN.text,
                  letterSpacing: "-0.02em",
                  mb: "6px",
                  fontFamily: SF,
                }}
              >
                모델
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: TOKEN.sub,
                  lineHeight: 1.55,
                  mb: "18px",
                  letterSpacing: "-0.01em",
                  fontFamily: SF,
                }}
              >
                다양한 알고리즘으로 AI 모델을 학습하세요. 실시간 모니터링과 자동화된 튜닝을 제공합니다.
              </Typography>
              <Box sx={{ mb: "18px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: "6px" }}>
                  <Typography sx={{ fontSize: "12px", color: TOKEN.sub, fontFamily: SF }}>현재 학습 진행률</Typography>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TOKEN.text, fontFamily: SF }}>67%</Typography>
                </Box>
                <Box
                  sx={{
                    height: 6,
                    background: "var(--dk-surface-3, #e8e8ed)",
                    borderRadius: "99px",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ height: 6, width: "67%", background: TOKEN.purple, borderRadius: "99px" }} />
                </Box>
              </Box>
              <MacButton id="naviagate-inspmodel-list" onClick={() => navigate("/main/inspection")}>
                <List size={14} style={{ marginRight: 6 }} /> 검사모델 메뉴로 이동
              </MacButton>
            </MacCard>
          </Grid>
        </Grid>

        {/* 서버 상태 */}
        <ServerStatusWidget />

        <Grid container spacing={1.5}>
          {[
            // {
            //   label: "데이터셋",
            //   path: "/main/dataset",
            //   desc: "학습 데이터를 관리하고 전처리하세요",
            //   iconBg: "#fff3e8",
            //   iconColor: TOKEN.orange,
            //   linkColor: TOKEN.orange,
            //   Icon: Database,
            // },
            {
              label: "검사모델",
              path: "/main/inspection",
              desc: "모델 성능을 검증하고 테스트하세요",
              iconBg: "#fee2e2",
              iconColor: "#dc2626",
              linkColor: "#dc2626",
              Icon: Users,
            },
            {
              label: "공지사항",
              path: "/main/notices",
              desc: "중요한 업데이트와 공지를 확인하세요",
              iconBg: "#fef9c3",
              iconColor: "#ca8a04",
              linkColor: "#ca8a04",
              Icon: TrendingUp,
            },
            {
              label: "설정",
              path: "/main/setting/user",
              desc: "시스템과 사용자 설정을 관리하세요",
              iconBg: "#f1f1f3",
              iconColor: TOKEN.sub,
              linkColor: TOKEN.sub,
              Icon: Settings,
            },
          ].map((s, i) => (
            <Grid item xs={4} key={i}>
              <MacCard
                onClick={() => navigate(s.path)}
                sx={{
                  p: "16px",
                  cursor: "pointer",
                  transition: "box-shadow 0.15s",
                  "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" },
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    background: s.iconBg,
                    color: s.iconColor,
                    ".dark &": { background: `color-mix(in srgb, ${s.iconColor} 20%, transparent)` },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: "12px",
                  }}
                >
                  <s.Icon size={18} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: TOKEN.text,
                    mb: "4px",
                    letterSpacing: "-0.01em",
                    fontFamily: SF,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: TOKEN.sub,
                    mb: "12px",
                    lineHeight: 1.4,
                    letterSpacing: "-0.01em",
                    fontFamily: SF,
                  }}
                >
                  {s.desc}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: s.linkColor }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 500, fontFamily: SF }}>바로가기</Typography>
                  <ArrowRight size={12} />
                </Box>
              </MacCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </PageShell>
  );
}
